import SyncIcon from '@mui/icons-material/Sync';
import {
  Box,
  CircularProgress,
  LinearProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { useNavigate } from 'react-router-dom';
import { DataTable, PageHeader, FilterTabs, StatusChip, Column } from '@/components/shared';
import useBreakpoint from '@/hooks/useBreakpoint';
import { ReceivablesOutlook } from '@/hooks/useReceivablesOutlook';
import {
  FxInvoiceSummary,
  FxClientInvoiceSummary,
  FxAgingBracket,
} from '@/services/financeInvoices';
import TrioButton from '@/trio-ui/Button';
import MetricCard from '@/trio-ui/MetricCard';
import SectionCard from '@/trio-ui/SectionCard';
import { FxInvoice, FxInvoiceStatus } from '@/types/api/finance';
import type { DateFilter, DatePreset } from './FinanceXInvoices.container';

type Props = {
  invoices: FxInvoice[];
  isLoading: boolean;
  statusFilter: FxInvoiceStatus | undefined;
  onStatusFilter: (status: FxInvoiceStatus | undefined) => void;
  dateFilter: DateFilter;
  onPresetChange: (preset: DatePreset) => void;
  onCustomDateChange: (startDate: string, endDate: string) => void;
  onSync: () => void;
  isSyncing: boolean;
  summary: FxInvoiceSummary;
  byClient: FxClientInvoiceSummary[];
  aging: FxAgingBracket[];
  outlook: ReceivablesOutlook;
};

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const statusMap = {
  paid: { label: 'Paid', color: '#10B981' },
  partial: { label: 'Partial', color: '#F59E0B' },
  open: { label: 'Open', color: '#054AD9' },
  overdue: { label: 'Overdue', color: '#EF4444' },
};

const MONTH_LABELS: Record<string, string> = {
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec',
};

const formatBillingPeriod = (period: string | null, invoiceDate: string): string => {
  const p = period ?? invoiceDate?.substring(0, 7);
  if (!p) return '—';
  const [yr, mo] = p.split('-');
  return `${MONTH_LABELS[mo] ?? mo} ${yr}`;
};

const columns: Column<FxInvoice>[] = [
  {
    id: 'invoiceNumber',
    label: '#',
    width: 70,
    render: (row) => (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {row.invoiceNumber}
      </Typography>
    ),
  },
  {
    id: 'date',
    label: 'Invoice Date',
    render: (row) => formatDate(row.date),
  },
  {
    id: 'billingPeriod',
    label: 'Billing Period',
    render: (row) => (
      <Typography variant="body2" sx={{ fontWeight: 500, color: '#054AD9' }}>
        {formatBillingPeriod(row.billingPeriod, row.date)}
      </Typography>
    ),
  },
  {
    id: 'clientName',
    label: 'Client',
    render: (row) => (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {row.clientName}
      </Typography>
    ),
  },
  {
    id: 'amount',
    label: 'Amount',
    align: 'right',
    render: (row) => (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {formatCurrency(row.amount)}
      </Typography>
    ),
  },
  {
    id: 'openBalance',
    label: 'Balance',
    align: 'right',
    render: (row) =>
      row.openBalance > 0 ? (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#EF4444' }}>
          {formatCurrency(row.openBalance)}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ color: '#71717A' }}>
          $0
        </Typography>
      ),
  },
  {
    id: 'dueDate',
    label: 'Due Date',
    render: (row) => formatDate(row.dueDate),
  },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    render: (row) => <StatusChip status={row.status} statusMap={statusMap} />,
  },
];

const FinanceXInvoicesComponent = function ({
  invoices,
  isLoading,
  statusFilter,
  onStatusFilter,
  dateFilter,
  onPresetChange,
  onCustomDateChange,
  onSync,
  isSyncing,
  summary,
  byClient,
  aging,
  outlook,
}: Props): JSX.Element {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  // Bars = cash due that week, line = cumulative. Overdue is red so it reads as
  // a problem rather than just another instalment.
  const cashTimelineOption = {
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        const wk = outlook.weeks[params[0]?.dataIndex ?? 0];
        if (!wk) return '';
        return [
          `<strong>${wk.label}</strong>`,
          `${wk.invoiceCount} invoice${wk.invoiceCount === 1 ? '' : 's'}`,
          `Due: ${formatCurrency(wk.amount)}`,
          `Cumulative: ${formatCurrency(wk.cumulative)}`,
        ].join('<br/>');
      },
    },
    grid: { left: 8, right: 8, top: 24, bottom: 8, containLabel: true },
    xAxis: {
      type: 'category',
      data: outlook.weeks.map((w) => w.label),
      axisLabel: { fontSize: 11, color: '#71717A' },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 11,
        color: '#71717A',
        formatter: (v: number) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`),
      },
      splitLine: { lineStyle: { color: 'hsla(0,0%,0%,0.08)' } },
    },
    series: [
      {
        type: 'bar',
        name: 'Due',
        data: outlook.weeks.map((w) => ({
          value: w.amount,
          itemStyle: { color: w.isOverdue ? '#EF4444' : '#054AD9', borderRadius: [4, 4, 0, 0] },
        })),
        barMaxWidth: 48,
      },
      {
        type: 'line',
        name: 'Cumulative',
        data: outlook.weeks.map((w) => w.cumulative),
        smooth: true,
        symbolSize: 6,
        lineStyle: { color: '#10B981', width: 2 },
        itemStyle: { color: '#10B981' },
      },
    ],
  };

  const tabs = [
    {
      key: 'all',
      label: 'All',
      count: Object.values(summary.countByStatus).reduce((a, b) => a + b, 0),
    },
    { key: 'open', label: 'Open', count: summary.countByStatus.open ?? 0 },
    { key: 'partial', label: 'Partial', count: summary.countByStatus.partial ?? 0 },
    { key: 'overdue', label: 'Overdue', count: summary.countByStatus.overdue ?? 0 },
    { key: 'paid', label: 'Paid', count: summary.countByStatus.paid ?? 0 },
  ];

  const maxClientInvoiced = Math.max(...byClient.map((c) => c.totalInvoiced), 1);

  return (
    <>
      <PageHeader
        title="Invoices"
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={dateFilter.preset}
              exclusive
              onChange={(_, val) => val && onPresetChange(val)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none', fontSize: 13, px: 1.5, py: 0.5, borderColor: '#E4E4E7',
                  '&.Mui-selected': { backgroundColor: '#054AD915', color: '#054AD9', borderColor: '#054AD9', '&:hover': { backgroundColor: '#054AD925' } },
                },
              }}
            >
              <ToggleButton value="this_month">This Month</ToggleButton>
              <ToggleButton value="last_month">Last Month</ToggleButton>
              <ToggleButton value="this_quarter">This Quarter</ToggleButton>
              <ToggleButton value="this_year">YTD</ToggleButton>
              <ToggleButton value="last_year">Last Year</ToggleButton>
              <ToggleButton value="custom">Custom</ToggleButton>
            </ToggleButtonGroup>
            {dateFilter.preset === 'custom' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField type="date" size="small" value={dateFilter.startDate} onChange={(e) => onCustomDateChange(e.target.value, dateFilter.endDate)} sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }} />
                <TextField type="date" size="small" value={dateFilter.endDate} onChange={(e) => onCustomDateChange(dateFilter.startDate, e.target.value)} sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }} />
              </Box>
            )}
            <Tooltip title="Fetch latest invoices from QuickBooks" arrow>
              <span>
                <TrioButton
                  variantColor="blue"
                  size="small"
                  onClick={onSync}
                  disabled={isSyncing}
                >
                  {isSyncing ? (
                    <CircularProgress size={16} sx={{ color: 'inherit', mr: 0.5 }} />
                  ) : (
                    <SyncIcon sx={{ fontSize: 18, mr: 0.5 }} />
                  )}
                  {isSyncing ? 'Syncing...' : 'Sync QB'}
                </TrioButton>
              </span>
            </Tooltip>
          </Box>
        }
      />

      {/* KPI Cards — reordered for cash planning: what is owed, what is late,
          and what should land in the next 30 days. Collected/Invoiced are
          historical and move to the right. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <MetricCard label="Open AR" value={formatCurrency(outlook.totalOpen)} color="#054AD9" />
        <MetricCard
          label={`Overdue (${outlook.overdueCount})`}
          value={formatCurrency(outlook.totalOverdue)}
          color="#EF4444"
        />
        <MetricCard
          label="Expected next 30 days"
          value={formatCurrency(outlook.next30Days)}
          color="#10B981"
        />
        <MetricCard
          label="Collected to date"
          value={formatCurrency(summary.totalPaid)}
          color="#71717A"
        />
      </Box>

      {/* Cash-in timeline — the forward view. Overdue is a separate red bar at
          the left rather than being folded into a week, because it is money that
          should already be here. */}
      {outlook.weeks.length > 0 && (
        <SectionCard title="Expected cash in">
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              By invoice due date, assuming clients pay on time — payment dates aren&apos;t tracked
              yet, so this is a schedule, not a forecast.
            </Typography>
          </Box>
          <ReactECharts
            style={{ height: isMobile ? 260 : 300 }}
            option={cashTimelineOption}
            notMerge
          />
        </SectionCard>
      )}

      {/* Who owes what, worst-first. Ranked by days late rather than amount: a
          small invoice five months old is a collectability problem, whereas a
          large one a week late is just timing. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <SectionCard title={`Overdue — ${formatCurrency(outlook.totalOverdue)}`}>
          {outlook.overdueInvoices.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Nothing overdue.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {outlook.overdueInvoices.map((inv) => (
                <Box
                  key={inv.id}
                  onClick={() => navigate(`/financex/invoices/${inv.shortId ?? inv.id}`)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 1.25,
                    px: 0.5,
                    cursor: 'pointer',
                    borderBottom: '1px solid hsla(0,0%,0%,0.04)',
                    transition: 'background 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
                    '&:hover': { background: '#F4F4F5' },
                    '&:active': { background: '#E4E4E7' },
                    '-webkit-tap-highlight-color': 'transparent',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }} noWrap>
                      {inv.clientName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      #{inv.invoiceNumber}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                    <Box
                      sx={{
                        fontSize: 11,
                        fontWeight: 500,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: '4px',
                        color: inv.daysLate > 60 ? '#7F1D1D' : '#B45309',
                        background: inv.daysLate > 60 ? '#FEE2E2' : '#FEF3C7',
                      }}
                    >
                      {inv.daysLate}d late
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>
                      {formatCurrency(inv.openBalance)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </SectionCard>

        {/* Concentration: how much of the receivable sits with one or two clients. */}
        <SectionCard title="AR concentration">
          {outlook.byClient.length > 0 && (
            <Box sx={{ px: 0.5, pb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ color: outlook.top2Pct >= 50 ? '#B45309' : '#71717A' }}
              >
                Top 2 clients hold <strong>{outlook.top2Pct.toFixed(1)}%</strong> of open AR
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {outlook.byClient.map((c) => (
              <Box key={c.clientName}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }} noWrap>
                    {c.clientName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {formatCurrency(c.openBalance)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: 13, color: '#71717A', minWidth: 44, textAlign: 'right' }}>
                      {c.pctOfAr.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
                {/* Full bar = share of AR; red portion = the part already late. */}
                <Box sx={{ display: 'flex', height: 6, borderRadius: '3px', overflow: 'hidden', background: '#F4F4F5' }}>
                  <Box sx={{ width: `${c.pctOfAr}%`, display: 'flex' }}>
                    <Box
                      sx={{
                        width: c.openBalance > 0 ? `${(c.overdue / c.openBalance) * 100}%` : '0%',
                        background: '#EF4444',
                      }}
                    />
                    <Box sx={{ flex: 1, background: '#054AD9' }} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </SectionCard>
      </Box>

      {/* Aging Report + Client Breakdown */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        {/* Aging Report */}
        <SectionCard title="Aging Report">
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 13,
              '& th': {
                textAlign: 'left',
                padding: '8px 12px',
                color: '#71717A',
                fontWeight: 500,
                fontSize: 12,
                borderBottom: '1px solid hsla(0,0%,0%,0.08)',
              },
              '& td': { padding: '8px 12px', borderBottom: '1px solid hsla(0,0%,0%,0.04)' },
            }}
          >
            <thead>
              <tr>
                <th>Period</th>
                <th style={{ textAlign: 'center' }}>Invoices</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {aging.map((bracket) => (
                <tr key={bracket.bracket}>
                  <td style={{ fontWeight: 500 }}>{bracket.bracket}</td>
                  <td style={{ textAlign: 'center' }}>{bracket.count}</td>
                  <td
                    style={{
                      textAlign: 'right',
                      fontWeight: 500,
                      color: bracket.amount > 0 ? '#EF4444' : '#71717A',
                    }}
                  >
                    {bracket.amount > 0 ? formatCurrency(bracket.amount) : '$0'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>
        </SectionCard>

        {/* Client Breakdown */}
        <SectionCard title="Revenue by Client">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {byClient.slice(0, 8).map((client) => (
              <Box key={client.clientName}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>
                    {client.clientName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {formatCurrency(client.totalInvoiced)}
                    </Typography>
                    {client.outstanding > 0 && (
                      <Typography variant="body2" sx={{ fontSize: 12, color: '#EF4444' }}>
                        {formatCurrency(client.outstanding)} due
                      </Typography>
                    )}
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(client.totalInvoiced / maxClientInvoiced) * 100}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#F4F4F5',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      backgroundColor: client.outstanding > 0 ? '#F59E0B' : '#054AD9',
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </SectionCard>
      </Box>

      {/* Invoice Table */}
      <FilterTabs
        tabs={tabs}
        activeKey={statusFilter ?? 'all'}
        onChange={(key) => onStatusFilter(key === 'all' ? undefined : (key as FxInvoiceStatus))}
      />

      <DataTable
        columns={columns}
        data={invoices}
        isLoading={isLoading}
        rowKey={(r) => r.id}
        searchPlaceholder="Search invoices..."
        searchFields={(r) => `${r.invoiceNumber} ${r.clientName}`}
        emptyMessage="No invoices found."
        onRowClick={(r) => navigate(`/financex/invoices/${r.shortId}`)}
      />
    </>
  );
};

export default FinanceXInvoicesComponent;
